import { Color3, Mesh, PBRMaterial, Quaternion, Scene, TransformNode, Vector3, VertexData } from '@babylonjs/core';
import type { Element, UnitDefinition } from '../core/types';

export interface HumanoidRig {
  root: TransformNode;
  torso: TransformNode;
  head: TransformNode;
  leftArm: TransformNode;
  rightArm: TransformNode;
  weapon: TransformNode;
  baseY: number;
  phase: number;
  dispose(): void;
}

const colors: Record<Element, [string, string]> = {
  Flame: ['#ff5b3a', '#ffbd45'], Frost: ['#62d7ff', '#d8f7ff'], Storm: ['#745dff', '#4de6ff'], Nature: ['#4dcc77', '#c4f25e'],
  Light: ['#fff0a3', '#ffffff'], Shadow: ['#9a4fd8', '#eb65ca'], Arcane: ['#d071ff', '#61a7ff'], Mechanical: ['#36e2cf', '#ffad41'], Neutral: ['#c3cad8', '#ffffff'],
};

function material(scene: Scene, name: string, hex: string, emissive = 0): PBRMaterial {
  const mat = new PBRMaterial(name, scene);
  mat.albedoColor = Color3.FromHexString(hex);
  mat.metallic = 0.18; mat.roughness = 0.62;
  if (emissive) { mat.emissiveColor = Color3.FromHexString(hex).scale(emissive); mat.metallic = 0.4; }
  return mat;
}

function tapered(scene: Scene, name: string, height: number, bottom: number, top: number, sides = 8): Mesh {
  const positions: number[] = []; const normals: number[] = []; const indices: number[] = []; const uvs: number[] = [];
  for (let ring = 0; ring < 2; ring += 1) {
    const y = (ring - 0.5) * height; const radius = ring ? top : bottom;
    for (let i = 0; i < sides; i += 1) {
      const angle = (i / sides) * Math.PI * 2; positions.push(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
      const normal = new Vector3(Math.cos(angle), (bottom - top) / height, Math.sin(angle)).normalize(); normals.push(normal.x, normal.y, normal.z); uvs.push(i / sides, ring);
    }
  }
  for (let i = 0; i < sides; i += 1) { const next = (i + 1) % sides; indices.push(i, next, sides + i, next, sides + next, sides + i); }
  const bottomCenter = positions.length / 3; positions.push(0, -height / 2, 0); normals.push(0, -1, 0); uvs.push(0.5, 0.5);
  const topCenter = positions.length / 3; positions.push(0, height / 2, 0); normals.push(0, 1, 0); uvs.push(0.5, 0.5);
  for (let i = 0; i < sides; i += 1) { const next = (i + 1) % sides; indices.push(bottomCenter, next, i, topCenter, sides + i, sides + next); }
  const mesh = new Mesh(name, scene); const data = new VertexData(); data.positions = positions; data.normals = normals; data.indices = indices; data.uvs = uvs; data.applyToMesh(mesh); return mesh;
}

function orb(scene: Scene, name: string, radius: number, rings = 6, sides = 10): Mesh {
  const positions: number[] = []; const normals: number[] = []; const indices: number[] = [];
  for (let r = 0; r <= rings; r += 1) {
    const phi = (r / rings) * Math.PI;
    for (let s = 0; s <= sides; s += 1) {
      const theta = (s / sides) * Math.PI * 2; const normal = new Vector3(Math.sin(phi) * Math.cos(theta), Math.cos(phi), Math.sin(phi) * Math.sin(theta));
      positions.push(normal.x * radius, normal.y * radius, normal.z * radius); normals.push(normal.x, normal.y, normal.z);
    }
  }
  for (let r = 0; r < rings; r += 1) for (let s = 0; s < sides; s += 1) { const a = r * (sides + 1) + s; const b = a + sides + 1; indices.push(a, b, a + 1, b, b + 1, a + 1); }
  const mesh = new Mesh(name, scene); const data = new VertexData(); data.positions = positions; data.normals = normals; data.indices = indices; data.applyToMesh(mesh); return mesh;
}

function diamond(scene: Scene, name: string, width: number, height: number, depth: number): Mesh {
  const p = [0, height / 2, 0, width / 2, 0, depth / 2, width / 2, 0, -depth / 2, -width / 2, 0, depth / 2, -width / 2, 0, -depth / 2, 0, -height / 2, 0];
  const i = [0,1,2, 0,3,1, 0,4,3, 0,2,4, 5,2,1, 5,1,3, 5,3,4, 5,4,2];
  const mesh = new Mesh(name, scene); const data = new VertexData(); data.positions = p; data.indices = i; const normals: number[] = []; VertexData.ComputeNormals(p, i, normals); data.normals = normals; data.applyToMesh(mesh); return mesh;
}

export class HumanoidFactory {
  constructor(private readonly scene: Scene) {}

  create(definition: Pick<UnitDefinition, 'name' | 'element' | 'weapon' | 'modelVariant'>, scale = 1): HumanoidRig {
    const root = new TransformNode(`rig-${definition.name}`, this.scene); root.scaling.setAll(scale);
    const [primaryHex, accentHex] = colors[definition.element];
    const primary = material(this.scene, `${definition.name}-primary`, primaryHex); const accent = material(this.scene, `${definition.name}-accent`, accentHex, 0.15); const dark = material(this.scene, `${definition.name}-dark`, '#11172e');
    const skin = material(this.scene, `${definition.name}-skin`, ['#f2c6a8', '#c88d72', '#e8b18c', '#8c5b4b'][definition.modelVariant % 4]!);

    const hips = new TransformNode('hips', this.scene); hips.parent = root; hips.position.y = 1.0;
    const torso = new TransformNode('torso', this.scene); torso.parent = hips; torso.position.y = 0.65;
    const chest = tapered(this.scene, 'tailored-armored-torso', 1.2, 0.42, 0.62, 7); chest.parent = torso; chest.material = primary;
    const chestCore = diamond(this.scene, 'astral-chest-core', 0.26, 0.4, 0.16); chestCore.parent = torso; chestCore.position.set(0, 0.18, -0.55); chestCore.material = accent;
    const belt = tapered(this.scene, 'layered-belt', 0.18, 0.46, 0.46, 8); belt.parent = torso; belt.position.y = -0.58; belt.material = dark;

    const head = new TransformNode('head', this.scene); head.parent = torso; head.position.y = 1.02;
    const face = orb(this.scene, 'stylized-face', 0.37, 6, 10); face.parent = head; face.scaling.z = 0.9; face.material = skin;
    const hair = tapered(this.scene, 'layered-hair-crown', 0.42, 0.42, 0.16, 9); hair.parent = head; hair.position.y = 0.28; hair.rotation.y = definition.modelVariant * 0.37; hair.material = dark;
    const eyeGlow = diamond(this.scene, 'visor-eyes', 0.34, 0.08, 0.04); eyeGlow.parent = head; eyeGlow.position.set(0, 0.03, -0.34); eyeGlow.material = accent;

    const arm = (side: -1 | 1): TransformNode => {
      const pivot = new TransformNode(side < 0 ? 'left-arm' : 'right-arm', this.scene); pivot.parent = torso; pivot.position.set(side * 0.65, 0.37, 0);
      const upper = tapered(this.scene, 'armored-upper-arm', 0.78, 0.19, 0.24, 7); upper.parent = pivot; upper.position.y = -0.34; upper.material = primary;
      const fore = tapered(this.scene, 'braced-forearm', 0.68, 0.15, 0.19, 7); fore.parent = pivot; fore.position.y = -0.98; fore.material = dark;
      const hand = orb(this.scene, 'hand', 0.16, 4, 7); hand.parent = pivot; hand.position.y = -1.35; hand.material = skin;
      return pivot;
    };
    const leftArm = arm(-1); const rightArm = arm(1);

    const leg = (side: -1 | 1) => {
      const pivot = new TransformNode(side < 0 ? 'left-leg' : 'right-leg', this.scene); pivot.parent = hips; pivot.position.x = side * 0.25;
      const thigh = tapered(this.scene, 'layered-thigh-armor', 0.94, 0.21, 0.28, 7); thigh.parent = pivot; thigh.position.y = -0.45; thigh.material = primary;
      const boot = tapered(this.scene, 'combat-boot', 0.9, 0.18, 0.22, 7); boot.parent = pivot; boot.position.y = -1.35; boot.material = dark;
    };
    leg(-1); leg(1);

    const cape = diamond(this.scene, 'split-astral-cape', 1.25, 1.7, 0.08); cape.parent = torso; cape.position.set(0, -0.25, 0.46); cape.rotation.x = -0.08; cape.material = primary;
    const weapon = new TransformNode('weapon', this.scene); weapon.parent = rightArm; weapon.position.set(0, -1.32, 0);
    this.attachWeapon(weapon, definition.weapon, accent, dark);

    return { root, torso, head, leftArm, rightArm, weapon, baseY: root.position.y, phase: definition.modelVariant * 0.7, dispose: () => root.dispose(false, true) };
  }

  animateIdle(rig: HumanoidRig, time: number): void {
    const t = time * 0.001 + rig.phase; rig.root.position.y = rig.baseY + Math.sin(t * 2) * 0.035;
    rig.torso.rotationQuaternion = Quaternion.FromEulerAngles(0, Math.sin(t * 0.55) * 0.04, Math.sin(t * 1.3) * 0.015);
    rig.leftArm.rotationQuaternion = Quaternion.FromEulerAngles(Math.sin(t * 1.8) * 0.07, 0, 0.12);
    rig.rightArm.rotationQuaternion = Quaternion.FromEulerAngles(-Math.sin(t * 1.8) * 0.07, 0, -0.12);
  }

  attackPose(rig: HumanoidRig, progress: number): void {
    const arc = Math.sin(Math.min(1, progress) * Math.PI);
    rig.rightArm.rotationQuaternion = Quaternion.FromEulerAngles(-1.7 * arc, 0.5 * arc, -0.25);
    rig.torso.rotationQuaternion = Quaternion.FromEulerAngles(0, -0.5 * arc, 0);
  }

  private attachWeapon(root: TransformNode, type: UnitDefinition['weapon'], accent: PBRMaterial, dark: PBRMaterial): void {
    if (type === 'orb') { const gem = orb(this.scene, 'chronicle-orb', 0.32, 5, 8); gem.parent = root; gem.material = accent; return; }
    if (type === 'gauntlet') { const fist = diamond(this.scene, 'astral-gauntlet', 0.48, 0.56, 0.48); fist.parent = root; fist.material = accent; return; }
    if (type === 'shield') { const guard = diamond(this.scene, 'sun-ward-shield', 1.0, 1.35, 0.2); guard.parent = root; guard.position.x = -0.15; guard.material = accent; return; }
    const length = type === 'greatblade' || type === 'scythe' ? 2.4 : type === 'bow' ? 1.8 : type === 'rifle' ? 1.75 : 1.7;
    const shaft = tapered(this.scene, `${type}-grip`, length, 0.07, 0.09, 6); shaft.parent = root; shaft.position.y = -length * 0.35; shaft.material = dark;
    const edge = diamond(this.scene, `${type}-energy-edge`, type === 'rifle' ? 0.26 : 0.42, type === 'bow' ? 0.9 : length * 0.7, 0.12); edge.parent = root; edge.position.y = -length * 1.05; edge.material = accent;
    if (type === 'scythe') { edge.rotation.z = Math.PI / 2; edge.position.x = 0.42; }
  }
}
